import { useState } from "react";
import type { MovimientoCaja, TipoMovimiento, CategoriaMovimiento } from "@/pages/cajaChica/types";

const categoriasIngreso: CategoriaMovimiento[] = [
  "Recaudación efectivo",
  "Aporte SUBDERE",
  "Otros ingresos",
];

const categoriasEgreso: CategoriaMovimiento[] = [
  "Materiales y herramientas",
  "Pago proveedor",
  "Servicios básicos",
  "Combustible",
  "Gastos administrativos",
  "Mantenimiento",
  "Otros egresos",
];

interface Props {
  onClose: () => void;
  onConfirm: (mov: MovimientoCaja) => void;
}

export default function NuevoMovimientoModal({ onClose, onConfirm }: Props) {
  const [tipo, setTipo] = useState<TipoMovimiento>("egreso");
  const [categoria, setCategoria] = useState<CategoriaMovimiento>("Materiales y herramientas");
  const [descripcion, setDescripcion] = useState("");
  const [monto, setMonto] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().split("T")[0]);
  const [responsable, setResponsable] = useState("");
  const [comprobante, setComprobante] = useState("");
  const [observacion, setObservacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const categorias = tipo === "ingreso" ? categoriasIngreso : categoriasEgreso;

  const handleTipoChange = (t: TipoMovimiento) => {
    setTipo(t);
    setCategoria(t === "ingreso" ? "Recaudación efectivo" : "Materiales y herramientas");
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!descripcion.trim()) e.descripcion = "Ingrese una descripción";
    if (!monto || isNaN(Number(monto.replace(/\./g, ""))) || Number(monto.replace(/\./g, "")) <= 0)
      e.monto = "Ingrese un monto válido";
    if (!responsable.trim()) e.responsable = "Ingrese el responsable";
    if (!fecha) e.fecha = "Seleccione una fecha";
    return e;
  };

  const formatMonto = (val: string) => {
    const num = val.replace(/\D/g, "");
    return num ? Number(num).toLocaleString("es-CL") : "";
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setLoading(true);
    setTimeout(() => {
      const nuevo: MovimientoCaja = {
        id: `MC-2026-${String(Date.now()).slice(-3)}`,
        fecha,
        tipo,
        categoria,
        descripcion: descripcion.trim(),
        monto: Number(monto.replace(/\./g, "")),
        responsable: responsable.trim(),
        comprobante: comprobante.trim() || null,
        observacion: observacion.trim() || null,
      };
      setLoading(false);
      setSuccess(true);
      setTimeout(() => {
        onConfirm(nuevo);
      }, 1400);
    }, 1200);
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-sm text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-4">
            <i className="ri-check-line text-3xl text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-1">Movimiento registrado</h3>
          <p className="text-sm text-gray-500">El movimiento fue guardado correctamente en caja chica.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-800">Nuevo Movimiento</h2>
            <p className="text-xs text-gray-400 mt-0.5">Registrar ingreso o egreso en caja chica</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Tipo selector */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-2">Tipo de movimiento</label>
            <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
              {(["ingreso", "egreso"] as TipoMovimiento[]).map((t) => (
                <button
                  key={t}
                  onClick={() => handleTipoChange(t)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                    tipo === t
                      ? t === "ingreso"
                        ? "bg-white text-emerald-700 shadow-sm"
                        : "bg-white text-red-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className={tipo === t ? (t === "ingreso" ? "ri-arrow-down-circle-line" : "ri-arrow-up-circle-line") : (t === "ingreso" ? "ri-arrow-down-circle-line" : "ri-arrow-up-circle-line")} />
                  {t === "ingreso" ? "Ingreso" : "Egreso"}
                </button>
              ))}
            </div>
          </div>

          {/* Categoría */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Categoría</label>
            <select
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as CategoriaMovimiento)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors bg-white cursor-pointer"
            >
              {categorias.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Descripción */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Descripción</label>
            <input
              type="text"
              value={descripcion}
              onChange={(e) => { setDescripcion(e.target.value); setErrors((p) => ({ ...p, descripcion: "" })); }}
              placeholder="Ej: Compra materiales ferretería..."
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-teal-400 transition-colors ${errors.descripcion ? "border-red-300" : "border-gray-200"}`}
            />
            {errors.descripcion && <p className="text-xs text-red-500 mt-1">{errors.descripcion}</p>}
          </div>

          {/* Monto + Fecha */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Monto ($)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                <input
                  type="text"
                  value={monto}
                  onChange={(e) => {
                    setMonto(formatMonto(e.target.value));
                    setErrors((p) => ({ ...p, monto: "" }));
                  }}
                  placeholder="0"
                  className={`w-full pl-7 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-teal-400 transition-colors ${errors.monto ? "border-red-300" : "border-gray-200"}`}
                />
              </div>
              {errors.monto && <p className="text-xs text-red-500 mt-1">{errors.monto}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => { setFecha(e.target.value); setErrors((p) => ({ ...p, fecha: "" })); }}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-teal-400 transition-colors ${errors.fecha ? "border-red-300" : "border-gray-200"}`}
              />
              {errors.fecha && <p className="text-xs text-red-500 mt-1">{errors.fecha}</p>}
            </div>
          </div>

          {/* Responsable + Comprobante */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Responsable</label>
              <input
                type="text"
                value={responsable}
                onChange={(e) => { setResponsable(e.target.value); setErrors((p) => ({ ...p, responsable: "" })); }}
                placeholder="Nombre del responsable"
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:border-teal-400 transition-colors ${errors.responsable ? "border-red-300" : "border-gray-200"}`}
              />
              {errors.responsable && <p className="text-xs text-red-500 mt-1">{errors.responsable}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">N° Comprobante <span className="text-gray-400 font-normal">(opcional)</span></label>
              <input
                type="text"
                value={comprobante}
                onChange={(e) => setComprobante(e.target.value)}
                placeholder="FAC-0001, BOL-0001..."
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors"
              />
            </div>
          </div>

          {/* Observación */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Observación <span className="text-gray-400 font-normal">(opcional)</span></label>
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Notas adicionales..."
              rows={2}
              maxLength={500}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 transition-colors resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              tipo === "ingreso"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-red-500 hover:bg-red-600"
            } disabled:opacity-60`}
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <i className={tipo === "ingreso" ? "ri-arrow-down-circle-line" : "ri-arrow-up-circle-line"} />
                Registrar {tipo === "ingreso" ? "ingreso" : "egreso"}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
